import { FLOW_DEFINITION_VERSION } from './constants';

export function cloneDefinition(def) {
  return JSON.parse(JSON.stringify(def));
}

export function createDefaultDefinition() {
  return {
    version: FLOW_DEFINITION_VERSION,
    start_node_id: 'start',
    nodes: {
      start: { type: 'start', next: 'welcome' },
      welcome: {
        type: 'message',
        content: 'Welcome',
        next: 'end',
      },
      end: { type: 'end' },
    },
  };
}

export function generateNodeId(nodes) {
  let i = 1;
  const keys = Object.keys(nodes || {});
  let id = `step_${i}`;
  while (keys.includes(id)) {
    i += 1;
    id = `step_${i}`;
  }
  return id;
}

/** BFS order (same as FlowDefinitionTimeline) */
export function getOrderedNodeIds(def) {
  if (!def?.nodes || !def.start_node_id) return [];
  const nodes = def.nodes;
  const start = String(def.start_node_id);
  const queue = [start];
  const ordered = [];
  const seen = new Set();

  while (queue.length) {
    const id = queue.shift();
    if (!seen.has(id) && nodes[id]) {
      seen.add(id);
      ordered.push(id);
      const n = nodes[id];
      if (n.next) queue.push(String(n.next));
      if (n.default_next) queue.push(String(n.default_next));
      if (Array.isArray(n.branches)) {
        n.branches.forEach(b => {
          if (b?.next) queue.push(String(b.next));
        });
      }
    }
  }
  return ordered;
}

/**
 * Linear spine from start following only `next` until end, wait_input, or missing next.
 */
export function getLinearSpineIds(def) {
  if (!def?.nodes || !def.start_node_id) return [];
  const nodes = def.nodes;
  let cur = String(def.start_node_id);
  const path = [];
  const guard = new Set();
  while (cur && nodes[cur] && !guard.has(cur)) {
    guard.add(cur);
    path.push(cur);
    const n = nodes[cur];
    if (n.type === 'end') break;
    if (n.type === 'wait_input') break;
    if (!n.next) break;
    cur = String(n.next);
  }
  return path;
}

export function definitionHasWaitInput(def) {
  if (!def?.nodes) return false;
  return Object.values(def.nodes).some(n => n?.type === 'wait_input');
}

/** True when Start → … → End following only `next` hits End (no wait_input on that chain). */
export function isMainSpineLinear(def) {
  if (!def?.nodes || !def.start_node_id) return false;
  const nodes = def.nodes;
  let cur = String(def.start_node_id);
  const seen = new Set();
  while (cur && nodes[cur]) {
    if (seen.has(cur)) return false;
    seen.add(cur);
    const n = nodes[cur];
    if (n.type === 'end') return true;
    if (n.type === 'wait_input') return false;
    if (!n.next) return false;
    cur = String(n.next);
  }
  return false;
}

/** Linear spine from Start to End with no extra disconnected nodes (safe for drag reorder). */
export function isStrictLinearFlow(def) {
  if (!isMainSpineLinear(def)) return false;
  const spine = getLinearSpineIds(def);
  return spine.length === Object.keys(def.nodes || {}).length;
}

function replaceRefsToTarget(nodes, oldId, newId) {
  const oid = String(oldId);
  const nid = String(newId);
  Object.keys(nodes).forEach(k => {
    const n = nodes[k];
    if (!n || typeof n !== 'object') return;
    if (n.next === oid) n.next = nid;
    if (n.default_next === oid) n.default_next = nid;
    if (Array.isArray(n.branches)) {
      n.branches.forEach(b => {
        if (b && b.next === oid) b.next = nid;
      });
    }
  });
}

/**
 * Remove node and point any reference to it to `fallbackNext` (default 'end').
 */
export function removeNode(def, nodeId, fallbackNext = 'end') {
  const d = cloneDefinition(def);
  const { nodes } = d;
  const nid = String(nodeId);
  if (nid === 'start' || nid === 'end' || !nodes[nid]) return d;

  const fb = String(fallbackNext);
  replaceRefsToTarget(nodes, nid, fb);
  delete nodes[nid];
  return d;
}

const defaultPayloadForType = type => {
  switch (type) {
    case 'message':
      return {
        type: 'message',
        content: 'New message',
        next: 'end',
        set_variables: {},
        message_content_attributes: {},
      };
    case 'wait_input':
      return {
        type: 'wait_input',
        capture_variable: '',
        prompt_type: 'text',
        prompt_options: [],
        branches: [{ match_type: 'equals', value: '', next: 'end' }],
        default_next: 'end',
      };
    case 'branch':
      return { type: 'branch', next: 'end' };
    case 'add_label':
      return { type: 'add_label', label: '', next: 'end' };
    case 'assign_team':
      return { type: 'assign_team', team_id: '', next: 'end' };
    case 'whatsapp_template':
      return { type: 'whatsapp_template', template_name: '', next: 'end' };
    default:
      return {
        type: 'message',
        content: 'New message',
        next: 'end',
        set_variables: {},
        message_content_attributes: {},
      };
  }
};

/**
 * Insert a new node after `afterId`, wiring afterId.next (or appropriate) to new node.
 * For wait_input, inserts after `default_next` chain head is not supported — use addNodeWithNext.
 */
export function insertNodeAfter(def, afterId, newType) {
  const d = cloneDefinition(def);
  const { nodes } = d;
  const aid = String(afterId);
  if (!nodes[aid]) return d;

  const newId = generateNodeId(nodes);
  const after = nodes[aid];
  const payload = defaultPayloadForType(newType);

  if (after.type === 'wait_input') {
    return d;
  }

  const oldNext = after.next ? String(after.next) : 'end';
  after.next = newId;
  if (newType === 'wait_input') {
    nodes[newId] = {
      ...payload,
      default_next: oldNext,
    };
  } else {
    nodes[newId] = { ...payload, next: oldNext };
  }
  return d;
}

/**
 * Append a message step before `end` on the linear spine (start → … → end).
 * Returns null if the spine hits wait_input before end (cannot auto-insert).
 */
export function insertMessageBeforeEnd(def) {
  const d = cloneDefinition(def);
  const { nodes } = d;
  const spine = getLinearSpineIds(d);
  const endIdx = spine.indexOf('end');
  if (endIdx <= 0) return null;
  const beforeEnd = spine[endIdx - 1];
  const n = nodes[beforeEnd];
  if (n.type === 'wait_input') return null;

  const newId = generateNodeId(nodes);
  const oldNext = n.next ? String(n.next) : 'end';
  n.next = newId;
  nodes[newId] = { type: 'message', content: '', next: oldNext };
  return d;
}

export function swapWithNextOnSpine(def, nodeId) {
  const d = cloneDefinition(def);
  const spine = getLinearSpineIds(d);
  const i = spine.indexOf(String(nodeId));
  if (i === -1 || i >= spine.length - 2) return d;
  const a = spine[i];
  const b = spine[i + 1];
  if (b === 'end') return d;
  const { nodes } = d;
  const prev = spine[i - 1];
  const afterB = nodes[b].next ? String(nodes[b].next) : 'end';

  nodes[prev].next = b;
  nodes[b].next = a;
  nodes[a].next = afterB;
  return d;
}

export function swapWithPreviousOnSpine(def, nodeId) {
  const spine = getLinearSpineIds(def);
  const i = spine.indexOf(String(nodeId));
  if (i <= 1) return cloneDefinition(def);
  return swapWithNextOnSpine(def, spine[i - 1]);
}

export function updateNodeField(def, nodeId, patch) {
  const d = cloneDefinition(def);
  const nid = String(nodeId);
  if (!d.nodes[nid]) return d;
  d.nodes[nid] = { ...d.nodes[nid], ...patch };
  return d;
}

/**
 * Rewire linear `next` chain: start → newMiddleIds[0] → … → last → end.
 * Only for definitions where the main spine is linear (no wait_input on spine).
 */
export function reorderLinearSpine(def, newMiddleIds) {
  const d = cloneDefinition(def);
  const spine = getLinearSpineIds(d);
  if (spine.length < 2) return d;
  const last = spine[spine.length - 1];
  if (last !== 'end') return d;
  const { nodes } = d;
  const startId = String(d.start_node_id);
  const mids = newMiddleIds.map(String).filter(id => id && id !== 'end');
  if (!nodes[startId]) return d;

  if (mids.length === 0) {
    nodes[startId].next = 'end';
    return d;
  }

  nodes[startId].next = mids[0];
  for (let i = 0; i < mids.length; i += 1) {
    const nid = mids[i];
    if (nodes[nid]) {
      const nxt = i < mids.length - 1 ? mids[i + 1] : 'end';
      if (nodes[nid].type === 'wait_input') return d;
      nodes[nid].next = nxt;
    }
  }
  return d;
}

export function setNodeType(def, nodeId, newType) {
  const d = cloneDefinition(def);
  const nid = String(nodeId);
  if (!d.nodes[nid] || nid === 'start' || nid === 'end') return d;

  const old = d.nodes[nid];
  const keepNext =
    old.type === 'wait_input' ? old.default_next || 'end' : old.next || 'end';

  const base = defaultPayloadForType(newType);
  if (newType === 'wait_input') {
    d.nodes[nid] = {
      ...base,
      default_next: keepNext,
    };
  } else {
    d.nodes[nid] = { ...base, next: keepNext };
  }
  return d;
}
